/**
 * Fartilo Cart Variant Selector + Mixed-Item Prevention
 * =====================================================
 * Surfaces: /cart page (CartItems CE) + cart drawer (CartDrawerItems CE)
 *
 * The Liquid already renders the selectors via server-side HTML.
 * This JS handles:
 *   1. Change events on model/size selects → cart line swap
 *   2. Change events on qty inputs → cart line quantity update
 *   3. Real-time mixed-item + large-order rule check on every cart mutation
 *
 * Priority hierarchy (additive, not mutually exclusive):
 *   1. 保护版全站拦截 – permanent maintenance mode (disabled attr + .cart__checkout-button--maintenance)
 *      → already enforced by Liquid/CSS; we do NOT override it
 *   2. 混装拦截 (this file) – unique variant count ≥ 2 → show warning, add .fartilo-rule-disabled
 *   3. 大单拦截 (this file) – total cart qty ≥ 10 → show warning, add .fartilo-rule-disabled
 *
 * We never remove the maintenance-mode disabled attr; we only toggle .fartilo-rule-disabled
 * and the warning banner visibility.
 *
 * v2 — Optimistic local updates (2026-08-12)
 * ------------------------------------------
 *  • changeLineQuantity / swapLineVariant responses are full cart JSON (contain
 *    total_price in cents, item line prices, quantities).  We use them to:
 *      - Instantly rewrite `.totals__total-value` (both page footer + drawer)
 *      - Instantly rewrite the row's `.cart-item__price-wrapper` (line subtotal)
 *      - Instantly update `data-quantity` on the row <tr>
 *  • Section-rendering API call is downgraded to a throttled background
 *    consistency sweep. It still runs, but no longer blocks user-perceived update.
 *  • Currency format: shop moneyWithCurrencyFormat = "${{amount}} USD".
 *    Prices come from Shopify as integer cents → divide by 100 → format.
 *    Line-item prices use plain "$X.XX" (no currency code), matching
 *    `| money` filter behaviour from the Liquid templates.
 */

(function () {
  'use strict';

  /* ─── Config ─────────────────────────────────────────────────────────── */

  const FARTILO_HANDLES = new Set([
    'fartilo-799-wiper-blade',
    'fartilo-818-wiper-blade',
  ]);

  const LARGE_ORDER_THRESHOLD = 10; // total boxes
  const DEBOUNCE_MS = 150;

  /* ─── Utilities ──────────────────────────────────────────────────────── */

  function debounce(fn, ms) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  function variantDataMap() {
    try {
      const el = document.getElementById('fartilo-variant-data');
      if (!el) return { variantMap: {}, modelSizeMap: {} };
      return JSON.parse(el.textContent);
    } catch (_) {
      return { variantMap: {}, modelSizeMap: {} };
    }
  }

  /* ─── Money formatting ───────────────────────────────────────────────── */

  /**
   * Format an integer-cents price as "$X.XX" (matches Shopify `| money` filter).
   * The shop moneyFormat is "${{amount}}" (USD store, confirmed via Admin API).
   */
  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    const dollars = (cents / 100).toFixed(2);
    // Insert thousands separator
    const parts = dollars.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  /**
   * Format an integer-cents price as "$X.XX USD"
   * (matches Shopify `| money_with_currency` filter for this store).
   */
  function formatMoneyWithCurrency(cents) {
    return formatMoney(cents) + ' USD';
  }

  /* ─── Optimistic DOM update ──────────────────────────────────────────── */

  /**
   * Apply cart JSON to the DOM immediately (optimistic update).
   * `cart`  – the full cart object returned by /cart/change.js or /cart/update.js
   *           (contains total_price in cents and items[] with final_line_price).
   * `lineKey` – the specific line key that changed (used to find the <tr> row).
   *
   * Updates:
   *   1. Total-price nodes in main-cart-footer and CartDrawer
   *   2. The changed line's subtotal cell
   *   3. data-quantity attribute on the row (for rule re-checks)
   */
  function applyOptimisticUpdate(cart, lineKey) {
    if (!cart || typeof cart.total_price !== 'number') return;

    const totalText = formatMoneyWithCurrency(cart.total_price);

    // 1. Update all `.totals__total-value` elements (page footer + drawer)
    document.querySelectorAll('.totals__total-value').forEach((el) => {
      el.textContent = totalText;
    });

    // 2. Update the changed line row's subtotal and data-quantity
    if (lineKey) {
      const changedItem = cart.items.find((i) => i.key === lineKey);
      if (changedItem) {
        // Find the row by data-key attribute
        const row = document.querySelector(`tr[data-key="${CSS.escape(lineKey)}"]`);
        if (row) {
          // Update data-quantity for rule-check reads
          row.setAttribute('data-quantity', String(changedItem.quantity));

          // Update line subtotal cell
          // Structure from main-cart-items.liquid:
          //   <td class="cart-item__totals ...">
          //     <div class="cart-item__price-wrapper">
          //       <span class="price price--end">$X.XX</span>   ← no-discount case
          //       OR <dl class="cart-item__discounted-prices">…   ← discount case
          //     </div>
          //   </td>
          const priceWrapper = row.querySelector('.cart-item__price-wrapper');
          if (priceWrapper) {
            const hasDiscount = changedItem.original_line_price !== changedItem.final_line_price;
            if (hasDiscount) {
              // Complex discount markup – update individual price targets if they exist;
              // fall back gracefully so next section-refresh corrects them.
              const origEl = priceWrapper.querySelector('.cart-item__old-price');
              const finalEl = priceWrapper.querySelector('.cart-item__final-price, dd.price.price--end');
              if (origEl) origEl.textContent = formatMoney(changedItem.original_line_price);
              if (finalEl) finalEl.textContent = formatMoney(changedItem.final_line_price);
            } else {
              // Simple case: update the single .price.price--end span
              const priceEl = priceWrapper.querySelector('.price.price--end');
              if (priceEl) {
                priceEl.textContent = formatMoney(changedItem.original_line_price);
              }
            }
          }
        }

        // Also update cart-drawer line rows if present (same data-key attribute)
        const drawerRow = document.querySelector(
          `#CartDrawer tr[data-key="${CSS.escape(lineKey)}"]`
        );
        if (drawerRow) {
          drawerRow.setAttribute('data-quantity', String(changedItem.quantity));
          const drawerPriceWrapper = drawerRow.querySelector('.cart-item__price-wrapper');
          if (drawerPriceWrapper) {
            const priceEl = drawerPriceWrapper.querySelector('.price.price--end');
            if (priceEl) priceEl.textContent = formatMoney(changedItem.final_line_price);
          }
        }
      }
    }
  }

  /* ─── Cart API helpers ───────────────────────────────────────────────── */

  function cartFetch(url, options) {
    return fetch(url, {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      ...options,
    });
  }

  function getCart() {
    return cartFetch('/cart.js').then((r) => r.json());
  }

  /**
   * Change quantity for a line item.
   * Returns full cart JSON (total_price in cents, items[]).
   */
  function changeLineQuantity(lineKey, quantity) {
    return cartFetch('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ id: lineKey, quantity: Math.max(1, quantity) }),
    }).then((r) => r.json());
  }

  /**
   * Swap a line item to a different variant.
   * Strategy: add new variant with same qty, then zero out old variant.
   * If newVariantId already exists as another line, we merge quantities.
   * Returns full cart JSON from the final /cart/update.js call.
   */
  function swapLineVariant(oldLineKey, newVariantId) {
    return getCart().then((cart) => {
      const oldLine = cart.items.find((i) => i.key === oldLineKey);
      if (!oldLine) return Promise.reject(new Error('Line not found: ' + oldLineKey));

      const qty = oldLine.quantity;
      const oldVariantId = oldLine.variant_id;

      // Check if new variant already in cart (different line)
      const existing = cart.items.find(
        (i) => i.variant_id === newVariantId && i.key !== oldLineKey
      );

      if (existing) {
        // Merge: set new variant qty = existing + old, then remove old
        const updates = {};
        updates[String(newVariantId)] = existing.quantity + qty;
        updates[String(oldVariantId)] = 0;
        return cartFetch('/cart/update.js', {
          method: 'POST',
          body: JSON.stringify({ updates }),
        }).then((r) => r.json());
      } else {
        // Add new variant first, then zero out old
        return cartFetch('/cart/add.js', {
          method: 'POST',
          body: JSON.stringify({ id: newVariantId, quantity: qty }),
        })
          .then(() => {
            const updates = {};
            updates[String(oldVariantId)] = 0;
            return cartFetch('/cart/update.js', {
              method: 'POST',
              body: JSON.stringify({ updates }),
            });
          })
          .then((r) => r.json());
      }
    });
  }

  /* ─── Cart state → rule check ────────────────────────────────────────── */

  /**
   * Returns { isMixed, isLarge, totalQty, uniqueVariantCount }
   * Used as the background consistency sweep for rule enforcement.
   */
  function checkRules() {
    return getCart().then((cart) => {
      let totalQty = 0;
      const uniqueVariants = new Set();

      cart.items.forEach((item) => {
        totalQty += item.quantity;
        // Only count fartilo products for mixed check
        if (FARTILO_HANDLES.has(item.handle)) {
          uniqueVariants.add(item.variant_id);
        }
      });

      return {
        isMixed: uniqueVariants.size >= 2,
        isLarge: totalQty >= LARGE_ORDER_THRESHOLD,
        totalQty,
        uniqueVariantCount: uniqueVariants.size,
      };
    });
  }

  /**
   * Derive rule state from a cart JSON object already in hand
   * (no extra network call needed).
   */
  function checkRulesFromCart(cart) {
    let totalQty = 0;
    const uniqueVariants = new Set();

    (cart.items || []).forEach((item) => {
      totalQty += item.quantity;
      if (FARTILO_HANDLES.has(item.handle)) {
        uniqueVariants.add(item.variant_id);
      }
    });

    return {
      isMixed: uniqueVariants.size >= 2,
      isLarge: totalQty >= LARGE_ORDER_THRESHOLD,
      totalQty,
      uniqueVariantCount: uniqueVariants.size,
    };
  }

  /* ─── UI update ───────────────────────────────────────────────────────── */

  function applyRuleState({ isMixed, isLarge }) {
    // Toggle mixed banners
    document.querySelectorAll('.fartilo-mixed-warning').forEach((el) => {
      el.hidden = !isMixed;
      el.setAttribute('aria-hidden', String(!isMixed));
    });

    // Toggle large-order banners
    document.querySelectorAll('.fartilo-large-warning').forEach((el) => {
      el.hidden = !isLarge;
      el.setAttribute('aria-hidden', String(!isLarge));
    });

    // Add/remove our rule-disabled class on checkout buttons
    // NOTE: maintenance mode already sets disabled + pointer-events:none;
    // our class just adds visual emphasis (opacity). We do not fight it.
    const shouldAddRuleClass = isMixed || isLarge;
    document.querySelectorAll('#checkout, #CartDrawer-Checkout').forEach((btn) => {
      btn.classList.toggle('fartilo-rule-disabled', shouldAddRuleClass);
    });
  }

  const debouncedCheck = debounce(() => {
    checkRules()
      .then(applyRuleState)
      .catch((err) => console.error('[Fartilo] rule check error:', err));
  }, DEBOUNCE_MS);

  /* ─── Background section refresh (throttled, non-blocking) ──────────── */

  let _sectionRefreshTimer = null;
  const SECTION_REFRESH_DELAY_MS = 800; // wait this long after last mutation

  /**
   * Schedule a background section-rendering sweep.
   * Multiple calls within SECTION_REFRESH_DELAY_MS coalesce into one fetch.
   * This is now purely a consistency guarantee — the user sees optimistic
   * updates immediately; this corrects any drift.
   */
  function scheduleSectionRefresh() {
    clearTimeout(_sectionRefreshTimer);
    _sectionRefreshTimer = setTimeout(() => {
      _runSectionRefresh();
    }, SECTION_REFRESH_DELAY_MS);
  }

  function _runSectionRefresh() {
    const sectionsToFetch = [];

    if (document.getElementById('main-cart-items')) {
      sectionsToFetch.push('main-cart-items', 'main-cart-footer');
    }
    if (document.getElementById('CartDrawer')) {
      sectionsToFetch.push('cart-drawer');
    }

    if (sectionsToFetch.length === 0) {
      debouncedCheck();
      return;
    }

    fetch(`/cart?sections=${sectionsToFetch.join(',')}`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((r) => r.json())
      .then((sectionData) => {
        updateSectionHTML(sectionData);
        setSelectorsLoading(false);
        debouncedCheck();
      })
      .catch((err) => {
        console.error('[Fartilo] background section refresh error:', err);
        setSelectorsLoading(false);
        debouncedCheck();
      });
  }

  function updateSectionHTML(sectionData) {
    const parser = new DOMParser();

    // Cart page: update cart-items wrapper
    const cartItemsHtml = sectionData['main-cart-items'];
    if (cartItemsHtml) {
      const doc = parser.parseFromString(cartItemsHtml, 'text/html');
      const newContents = doc.querySelector('.js-contents');
      const oldContents = document.querySelector('#main-cart-items .js-contents');
      if (newContents && oldContents) {
        oldContents.innerHTML = newContents.innerHTML;
      }
    }

    // Cart footer: update js-contents inside footer
    const cartFooterHtml = sectionData['main-cart-footer'];
    if (cartFooterHtml) {
      const doc = parser.parseFromString(cartFooterHtml, 'text/html');
      const newContents = doc.querySelector('.js-contents');
      const oldContents = document.querySelector('#main-cart-footer .js-contents');
      if (newContents && oldContents) {
        oldContents.innerHTML = newContents.innerHTML;
      }
      // Also update totals (belt-and-suspenders: js-contents swap above covers this,
      // but guard against themes where totals live outside .js-contents)
      const newTotals = doc.querySelector('.totals__total-value');
      const oldTotals = document.querySelector('#main-cart-footer .totals__total-value');
      if (newTotals && oldTotals) {
        oldTotals.textContent = newTotals.textContent;
      }
    }

    // Cart drawer: update drawer inner content
    const cartDrawerHtml = sectionData['cart-drawer'];
    if (cartDrawerHtml) {
      const doc = parser.parseFromString(cartDrawerHtml, 'text/html');
      const newDrawer = doc.getElementById('CartDrawer');
      const oldDrawer = document.getElementById('CartDrawer');
      if (newDrawer && oldDrawer) {
        oldDrawer.innerHTML = newDrawer.innerHTML;
      }
    }

    // Notify Shopify's cart pub-sub so other components (totals, etc.) update
    if (window.publish && window.PUB_SUB_EVENTS) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'fartilo-cart-variant-selector' });
    }
  }

  /* ─── Selector event handlers ────────────────────────────────────────── */

  function onModelChange(e) {
    const select = e.target;
    if (!select.classList.contains('fartilo-model-select')) return;

    const newModel = select.value;                   // '799' or '818'
    const currentSize = select.dataset.currentSize;  // e.g. '18'
    const lineKey = select.dataset.lineKey;

    const { modelSizeMap } = variantDataMap();
    const key = `${newModel}_${currentSize}`;
    const newVariantId = modelSizeMap[key];

    if (!newVariantId) {
      console.warn(`[Fartilo] No variant for model=${newModel} size=${currentSize}`);
      return;
    }

    setSelectorsLoading(true);
    swapLineVariant(lineKey, newVariantId)
      .then((cart) => {
        // Optimistic update: total + (swap lands on a new line key, so we can't
        // update the row directly without knowing the new key; update total only)
        if (cart && typeof cart.total_price === 'number') {
          applyOptimisticUpdate(cart, null);
          applyRuleState(checkRulesFromCart(cart));
        }
        scheduleSectionRefresh();
      })
      .catch((err) => {
        console.error('[Fartilo] model swap error:', err);
        setSelectorsLoading(false);
        scheduleSectionRefresh();
      });
  }

  function onSizeChange(e) {
    const select = e.target;
    if (!select.classList.contains('fartilo-size-select')) return;

    const newSize = select.value;                      // e.g. '20'
    const currentModel = select.dataset.currentModel;  // '799' or '818'
    const lineKey = select.dataset.lineKey;

    const { modelSizeMap } = variantDataMap();
    const key = `${currentModel}_${newSize}`;
    const newVariantId = modelSizeMap[key];

    if (!newVariantId) {
      console.warn(`[Fartilo] No variant for model=${currentModel} size=${newSize}`);
      return;
    }

    setSelectorsLoading(true);
    swapLineVariant(lineKey, newVariantId)
      .then((cart) => {
        if (cart && typeof cart.total_price === 'number') {
          applyOptimisticUpdate(cart, null);
          applyRuleState(checkRulesFromCart(cart));
        }
        scheduleSectionRefresh();
      })
      .catch((err) => {
        console.error('[Fartilo] size swap error:', err);
        setSelectorsLoading(false);
        scheduleSectionRefresh();
      });
  }

  const onQtyChange = debounce(function (e) {
    const input = e.target;
    if (!input.classList.contains('fartilo-qty-input')) return;

    const lineKey = input.dataset.lineKey;
    const newQty = Math.max(1, parseInt(input.value, 10) || 1);
    input.value = String(newQty);

    setSelectorsLoading(true);
    changeLineQuantity(lineKey, newQty)
      .then((cart) => {
        // Optimistic update: instantly reflect new total + line subtotal
        if (cart && typeof cart.total_price === 'number') {
          applyOptimisticUpdate(cart, lineKey);
          applyRuleState(checkRulesFromCart(cart));
        }
        // Unlock controls immediately so user can keep editing
        setSelectorsLoading(false);
        // Background consistency sweep (non-blocking)
        scheduleSectionRefresh();
      })
      .catch((err) => {
        console.error('[Fartilo] qty change error:', err);
        setSelectorsLoading(false);
        scheduleSectionRefresh();
      });
  }, DEBOUNCE_MS);

  /* ─── Loading state ───────────────────────────────────────────────────── */

  function setSelectorsLoading(loading) {
    document.querySelectorAll(
      '.fartilo-select, .fartilo-qty-input'
    ).forEach((el) => {
      el.disabled = loading;
    });
  }

  /* ─── Event delegation ───────────────────────────────────────────────── */

  function attachDelegatedEvents() {
    // Use document-level delegation so events still work after section re-renders
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('fartilo-model-select')) onModelChange(e);
      else if (e.target.classList.contains('fartilo-size-select')) onSizeChange(e);
      else if (e.target.classList.contains('fartilo-qty-input')) onQtyChange(e);
    });

    // Also listen to Shopify cart events for rule re-check
    document.addEventListener('cart:refresh', debouncedCheck);
    document.addEventListener('cart:updated', debouncedCheck);
  }

  /* ─── Observe cart for DOM-rendered section updates ──────────────────── */

  function observeCartUpdates() {
    // Watch for Shopify's CartItems CE re-renders
    const cartItemsEl = document.getElementById('main-cart-items');
    if (cartItemsEl) {
      new MutationObserver(debounce(debouncedCheck, 300))
        .observe(cartItemsEl, { childList: true, subtree: true });
    }

    // Watch for cart-drawer re-renders
    const drawer = document.getElementById('CartDrawer');
    if (drawer) {
      new MutationObserver(debounce(debouncedCheck, 300))
        .observe(drawer, { childList: true, subtree: true });
    }
  }

  /* ─── Init ────────────────────────────────────────────────────────────── */

  function init() {
    attachDelegatedEvents();
    observeCartUpdates();
    debouncedCheck(); // run initial check
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
